import { useEffect, useMemo, useRef } from "react";
import { useNavigate } from "react-router-dom";
import HomeTemplate from "./home-template";
import "./homepage.css";

const BODY_CLASS_NAMES = [
  "bg-background-light",
  "font-display",
  "text-slate-900",
  "selection:bg-primary/30",
  "overflow-x-hidden",
];

function HomePage() {
  const navigate = useNavigate();
  const rootRef = useRef<HTMLDivElement>(null);
  const cleanupRef = useRef<(() => void)[]>([]);

  const buttonRoutes = useMemo(
    () => ({
      "Log In": "/login",
      "Get Started": "/signup",
      "Send a Runner": "/deliverypage",
      "Send your first Runner": "/deliverypage",
      "Apply as a Runner →": "/signup?intent=runner",
      "Become a Provider →": "/signup?intent=provider",
    }),
    [],
  );

  useEffect(() => {
    document.title = "Errandi — Your Personal Runner";
    document.body.classList.add(...BODY_CLASS_NAMES);

    return () => {
      document.body.classList.remove(...BODY_CLASS_NAMES);
    };
  }, []);

  useEffect(() => {
    const rootElement = rootRef.current;

    cleanupRef.current.forEach((cleanup) => cleanup());
    cleanupRef.current = [];

    if (!rootElement) {
      return undefined;
    }

    const cleanups: (() => void)[] = [];

    const revealObserver = new IntersectionObserver(
      (entries, observer) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) {
            return;
          }

          const delay = Number.parseFloat((entry.target as HTMLElement).dataset.delay || "0");
          window.setTimeout(() => {
            entry.target.classList.add("visible");
          }, delay * 1000);
          observer.unobserve(entry.target);
        });
      },
      { threshold: 0.08 },
    );

    rootElement.querySelectorAll(".fade-up").forEach((element: Element) => {
      revealObserver.observe(element);
    });

    cleanups.push(() => revealObserver.disconnect());

    const counterObserver = new IntersectionObserver(
      (entries, observer) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) {
            return;
          }

          const counterElement = entry.target.querySelector("[data-target]") as HTMLElement | null;
          if (!counterElement || counterElement.dataset.animated) {
            observer.unobserve(entry.target);
            return;
          }

          counterElement.dataset.animated = "true";
          const target = Number.parseInt(
            counterElement.dataset.target || "0",
            10,
          );
          const suffix = counterElement.dataset.suffix || "";
          const prefix = counterElement.dataset.prefix || "";
          const duration = 1400;
          const start = performance.now();

          const updateCounter = (now: number) => {
            const progress = Math.min((now - start) / duration, 1);
            const eased = progress === 1 ? 1 : 1 - 2 ** (-10 * progress);
            const value = Math.round(target * eased);
            counterElement.textContent = `${prefix}${value}${suffix}`;

            if (progress < 1) {
              window.requestAnimationFrame(updateCounter);
            }
          };

          window.requestAnimationFrame(updateCounter);
          observer.unobserve(entry.target);
        });
      },
      { threshold: 0.5 },
    );

    rootElement.querySelectorAll(".stat-card").forEach((element: Element) => {
      counterObserver.observe(element);
    });

    cleanups.push(() => counterObserver.disconnect());

    rootElement.querySelectorAll<HTMLElement>(".card-hover").forEach((card) => {
      const handleMouseMove = (event: MouseEvent) => {
        const rect = card.getBoundingClientRect();
        const x = (event.clientX - rect.left) / rect.width - 0.5;
        const y = (event.clientY - rect.top) / rect.height - 0.5;
        card.style.transform = `perspective(800px) rotateY(${x * 6}deg) rotateX(${-y * 6}deg) translateY(-4px)`;
      };

      const handleMouseLeave = () => {
        card.style.transition =
          "transform 0.5s cubic-bezier(0.34,1.56,0.64,1), box-shadow 0.3s ease";
        card.style.transform =
          "perspective(800px) rotateY(0deg) rotateX(0deg) translateY(0)";
      };

      const handleMouseEnter = () => {
        card.style.transition = "transform 0.15s ease, box-shadow 0.3s ease";
      };

      card.addEventListener("mousemove", handleMouseMove);
      card.addEventListener("mouseleave", handleMouseLeave);
      card.addEventListener("mouseenter", handleMouseEnter);

      cleanups.push(() => {
        card.removeEventListener("mousemove", handleMouseMove);
        card.removeEventListener("mouseleave", handleMouseLeave);
        card.removeEventListener("mouseenter", handleMouseEnter);
      });
    });

    const handleDocumentClick = (event: MouseEvent) => {
      const anchor = (event.target as HTMLElement).closest('a[href^="#"]');
      if (anchor) {
        const targetId = anchor.getAttribute("href");
        const targetElement = targetId
          ? document.querySelector(targetId)
          : null;
        if (targetElement) {
          event.preventDefault();
          targetElement.scrollIntoView({ behavior: "smooth", block: "start" });
        }
        return;
      }

      const button = (event.target as HTMLElement).closest("button");
      if (!button) {
        return;
      }

      const label = button.textContent?.replace(/\s+/g, " ").trim();
      if (!label) {
        return;
      }

      if (label === "Watch how it works ↓") {
        const targetElement = document.querySelector("#how");
        targetElement?.scrollIntoView({ behavior: "smooth", block: "start" });
        return;
      }

      if (label === "Become a Runner") {
        const targetElement = document.querySelector("#become");
        targetElement?.scrollIntoView({ behavior: "smooth", block: "start" });
        return;
      }

      const route = buttonRoutes[label as keyof typeof buttonRoutes];
      if (typeof route === "string" && route.length > 0) {
        navigate(route);
      }
    };

    rootElement.addEventListener("click", handleDocumentClick);
    cleanups.push(() =>
      rootElement.removeEventListener("click", handleDocumentClick),
    );

    const handleScroll = () => {
      const scrolled = window.scrollY;
      rootElement.querySelectorAll<HTMLElement>(".orb-drift").forEach((element, index) => {
        const direction = index % 2 === 0 ? 1 : -1;
        element.style.transform = `translateY(${scrolled * 0.12 * direction}px)`;
      });
    };

    window.addEventListener("scroll", handleScroll, { passive: true });
    cleanups.push(() => window.removeEventListener("scroll", handleScroll));

    cleanupRef.current = cleanups;

    return () => {
      cleanups.forEach((cleanup) => cleanup());
    };
  }, [buttonRoutes, navigate]);

  return (
    <div ref={rootRef}>
      <HomeTemplate />
    </div>
  );
}

export default HomePage;
